import { Form, Input, Select, Switch, Typography } from 'antd';
import { DatePicker, DataCard } from 'components';
import { CardField } from 'components/DataCard/ui/Field';
import { getAllExperience } from 'components/TimeLineComp/helpers/getAllExperience';
import TimeLineComp from 'components/TimeLineComp/TimeLineComp';
import dayjs from 'dayjs';
import { useOutletContext } from 'hooks/useOutletContext';
import type { Mutation } from 'models/helper';
import { Role, RoleList, User } from 'models/user';
import { usersApi } from 'services/rtk/users';

interface MainCardProps {
  userData: User;
}

const MainCard = ({ userData }: MainCardProps) => {
  const { role, id: meId } = useOutletContext();

  const isAdmin = role === 'ADMIN';
  const isBirthdayEditAllowed = ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD'].includes(role);
  const isRoleEditAllowed =
    isAdmin ||
    (meId !== userData.id &&
      ((role === 'PARTNER' && userData.role !== 'PARTNER') || (role === 'HEAD' && userData.role !== 'HEAD')));
  const isEditAllowed = isBirthdayEditAllowed || isRoleEditAllowed;

  const editUser = usersApi.useUpdateUserMutation();

  const [form] = Form.useForm();

  const handleChangeExperience = (index: number, field: 'start' | 'end', value: dayjs.Dayjs | null) => {
    if (!value) return;
    const updated = [...form.getFieldValue('experience')];
    updated[index] = {
      ...updated[index],
      [field]: value.format('YYYY-MM-DDTHH:mm:ss'),
    };
    form.setFieldValue('experience', updated);
  };

  const fields: CardField[] = [
    {
      editable: isAdmin,
      dataIndex: 'name',
      input: <Input id="me-change-user-info-name-input" placeholder="Введите имя" />,
      label: 'Имя',
      value: userData.name,
    },
    {
      editable: isRoleEditAllowed,
      dataIndex: 'role',
      input: (
        <Select
          id="me-change-user-info-role-select"
          placeholder="Выберите должность"
          options={getUserSelectOptions(role)}
        />
      ),
      label: 'Должность',
      value: userData.role,
    },
    {
      editable: isAdmin,
      dataIndex: 'email',
      input: <Input type="email" id="me-change-user-info-email-input" placeholder="Введите email" />,
      label: 'Email',
      value: userData.email,
    },
    {
      editable: isAdmin,
      dataIndex: 'isActive',
      input: <Switch id="me-change-user-info-activity-switch" />,
      label: 'Активность',
      value: userData.isActive,
      valuePropName: 'checked',
    },
    {
      editable: isBirthdayEditAllowed,
      dataIndex: 'birthday',
      input: <DatePicker id="me-change-user-info-birthday-datepicker" />,
      label: 'Дата рождения',
      value: userData.birthday ? dayjs(userData.birthday).format('DD-MM-YYYY') : '',
    },
    {
      editable: isAdmin,
      dataIndex: 'phone',
      input: <Input id="me-change-user-info-phone-number-input" placeholder="Введите номер телефона" />,
      label: 'Номер телефона',
      value: userData.phone ? userData.phone : '',
      visible: isAdmin,
    },
    {
      editable: isAdmin,
      dataIndex: 'telegram',
      input: <Input id="me-change-user-info-telegram-input" prefix={'@'} placeholder="Введите телеграм" />,
      label: 'Телеграм',
      value: userData.telegram ? (
        <Typography.Link
          href={`https://t.me/${userData.telegram}`}
        >{`@${userData.telegram}`}</Typography.Link>
      ) : (
        ''
      ),
      visible: isAdmin,
    },
    {
      label: 'Общий стаж',
      value: getAllExperience(userData.experience),
      visible: isAdmin,
    },
  ];

  if (userData.experience && userData.experience[0]?.end === null) {
    fields.push({
      label: 'Дата начала работы',
      value: userData.experience[0].start ? dayjs(userData.experience[0].start).format('DD MMMM YYYY') : '',
      visible: isAdmin,
    });
  } else {
    if (userData.experience[0]) {
      fields.push({
        editable: isAdmin,
        dataIndex: 'experience',
        label: 'График работы',
        value: <TimeLineComp mode={'view'} userData={userData} />,
        input: <TimeLineComp mode={'edit'} onChangeExperience={handleChangeExperience} userData={userData} />,
        visible: isAdmin,
      });
    }
  }

  if (isAdmin && !userData.isActive) {
    fields.push({
      dataIndex: 'dismissalReason',
      input: (
        <Input.TextArea defaultValue={userData.dismissal?.reason} placeholder="Введите причину увольнения" />
      ),
      label: 'Причина увольнения',
      value: userData.dismissal?.reason,
      editable: true,
    });
  }

  return (
    <>
      {isEditAllowed ? (
        <DataCard
          allowEdit={true}
          title="Основная информация"
          data={{ ...userData }}
          fields={fields}
          mutation={editUser as Mutation}
          withId
          externalForm={form}
        />
      ) : (
        <DataCard allowEdit={false} title="Основная информация" fields={fields} />
      )}
    </>
  );
};

export default MainCard;

const getUserSelectOptionValues = (role: Role): Role[] => {
  const validRoles: Role[] = ['ADMIN', 'PARTNER', 'HEAD'];

  if (!validRoles.includes(role)) {
    return [];
  }

  if (role === 'ADMIN') {
    return RoleList.filter((role) => role !== 'ADMIN' && role !== 'NEW_USER');
  }

  return RoleList.filter((role) => role === 'BUYER' || role === 'TEAM_LEAD');
};

const getUserSelectOptions = (role: Role) => {
  const values = getUserSelectOptionValues(role);
  return values.map((r) => ({ key: r, value: r }));
};
